import { inject, Injectable } from '@angular/core';
import { WidgetConfig, WidgetType } from '../grid-widget-config.service';
import { UnitStyle } from '../../models/unit';
import { IndicatorStore } from '../../stores/indicator/indicator.state';
import { GlobalsStore } from '../../stores/globals.state';
import { WegweiserStore } from '../../stores/wegweiser-api/wegweiser.state';

@Injectable({
  providedIn: 'root',
})
export class TooltipHelperService {
  readonly indicatorStore = inject(IndicatorStore);
  readonly globalsStore = inject(GlobalsStore);

  /**
   * Creates or updates a tooltip within a specified container, updating its
   * content and position based on the provided configuration and tooltip data.
   *
   * @param {any} tooltip - The tooltip data object used to determine content
   *                         and visibility.
   * @param {string} tooltipContainerId - The ID of the container element where
   *                                       the tooltip will be rendered.
   * @param {WidgetType} type - The type of widget associated with the tooltip,
   *                            used for generating content.
   * @param {WidgetConfig} config - The configuration object for the widget,
   *                                used to customize tooltip behavior.
   * @return {void} This method does not return a value.
   */
  createOrUpdateTooltip(
    tooltip: any,
    tooltipContainerId: string,
    type: WidgetType,
    config: WidgetConfig,
    signeAxisType: boolean
  ): void {
    if (!tooltip) return;

    const tooltipContainer = document.getElementById(tooltipContainerId);
    if (!tooltipContainer) {
      console.error('Tooltip container not found:', tooltipContainerId);
      return;
    }

    let tooltipEl = tooltipContainer.querySelector(
      '.widget-tooltip'
    ) as HTMLElement;

    // Create tooltip element if not already present
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.className = 'widget-tooltip';
      tooltipEl.classList.add(
        'p-3',
        'text-xs',
        'text-0',
        'pointer-events-none',
        'border-round',
        'flex',
        'flex-column',
        'gap-2'
      );
      tooltipContainer.appendChild(tooltipEl);
    }

    // Hide tooltip if not visible
    if (tooltip.opacity === 0) {
      tooltipEl.classList.remove('visible');
      return;
    }

    // Get tooltip title and body content
    const title = this.getTooltipTitle(tooltip.dataPoints);
    const body = this.getTooltipBody(
      tooltip.dataPoints,
      type,
      config,
      tooltip,
      signeAxisType
    );

    // Update tooltip content
    tooltipEl.innerHTML = `
    <h4 class="text-base tooltip-title m-0">${title}</h4>
    <div class="tooltip-body">${body}</div>
  `;

    // Position and display tooltip
    this.positionTooltip(tooltipEl, tooltip);
    tooltipEl.classList.add('visible');
  }

  private getTooltipTitle(tooltipItems: any[]): string {
    if (!tooltipItems || !tooltipItems[0]) {
      return '';
    }
    const dataset = tooltipItems[0]?.dataset;
    // Handle regression title
    if (dataset?.regression) {
      return 'Regression';
    }
    // Default to dataset label
    return tooltipItems[0]?.label || '';
  }

  /**
   * Generates the tooltip body content as a string based on the provided tooltip items,
   * widget type, configuration, and tooltip object.
   *
   * @param {any[]} tooltipItems - An array of tooltip items used for generating the tooltip content.
   * @param {WidgetType} type - The type of widget (e.g., chart type) to determine the tooltip formatting.
   * @param {WidgetConfig} config - The configuration of the widget containing options for customization.
   * @param {any} tooltip - The tooltip object containing relevant details, like label colors.
   * @return {string} A string representing the formatted tooltip body with necessary HTML structure and styling.
   */
  private getTooltipBody(
    tooltipItems: any[],
    type: WidgetType,
    config: WidgetConfig,
    tooltip: any,
    signeAxisType: boolean
  ): string {
    return tooltipItems
      .map((tooltipItem: any, index: number) => {
        const dataset = tooltipItem.dataset;
        const customTooltip = dataset?.customTooltip || dataset?.label; // Prefer customTooltip
        const color =
          (Array.isArray(dataset.backgroundColor)
            ? dataset.backgroundColor?.at(tooltipItem.dataIndex)
            : dataset.backgroundColor) ||
          tooltip.labelColors[index]?.backgroundColor ||
          'transparent'; // Dataset's color

        // Format values based on chart type
        const tooltipValue = this.getFormattedTooltipValue(
          tooltipItem.raw,
          type,
          config,
          tooltipItem
        );

        // Handle special cases like scatter or bubble charts
        if (
          (type === 'scatter' || type === 'bubble') &&
          Array.isArray(tooltipValue)
        ) {
          const tooltipEntries = this.buildScatterBubbleTooltip(
            tooltipValue,
            dataset,
            type
          );
          return `
        <div class="flex gap-2 flex-column">
          ${tooltipEntries.map(e => `<span>${e}</span>`).join('')}
        </div>
      `;
        }

        // General case: return tooltips with color and value
        return `
      <div class="flex gap-2 align-items-baseline">
        <i class="fas fa-circle" style="color: ${color}"></i>
        <span><b>${customTooltip}:</b> ${tooltipValue}</span>
      </div>
    `;
      })
      .join(''); // Combine all tooltip lines
  }

  /**
   * Builds tooltips for scatter and bubble chart data points.
   *
   * @param {string[]} values - Array of values representing data point attributes.
   * @param {any} dataset - The dataset object containing additional information about the chart.
   * @param {WidgetType} type - The type of widget, either 'scatter' or 'bubble'.
   * @return {string[]} An array of strings representing the tooltip entries after processing.
   */
  private buildScatterBubbleTooltip(
    values: string[],
    dataset: any,
    type: WidgetType
  ): string[] {
    const tooltipEntries = [...Array(values.length)].map(() => ''); // Placeholder entries

    // Map x/y and other dataset values
    tooltipEntries[0] = `${values.at(0)}`; // X-value
    tooltipEntries[1] = `${values.at(1)}`; // Y-value

    if (dataset.regression) {
      tooltipEntries[2] = `${values.at(2)}`;
    } else if (type === 'bubble') {
      tooltipEntries[3] = `${values.at(3)}`;
    }

    return tooltipEntries.filter(e => !!e); // Remove empty entries
  }

  private positionTooltip(tooltipEl: HTMLElement, tooltip: any): void {
    const position = tooltip.chart.canvas.getBoundingClientRect();
    tooltipEl.style.left =
      position.left +
      window.scrollX +
      tooltip.caretX -
      tooltipEl.offsetWidth / 2 +
      'px';
    tooltipEl.style.top =
      position.top +
      window.scrollY +
      tooltip.caretY -
      tooltipEl.offsetHeight -
      10 +
      'px'; // Arrow offset
  }

  /**
   * Formats the tooltip value based on the provided configuration and dataset properties.
   *
   * @param {any} value - The value to be formatted for the tooltip.
   * @param {string} type - The type of the widget.
   * @param {WidgetConfig} config - The widget configuration object containing formatting and indicator details.
   * @param {any} tooltipItem - The tooltip item containing dataset-specific properties for rendering.
   * @param {UnitStyle} [unitStyle='short'] - The style for formatting the unit, defaults to 'short'.
   * @return {string} Returns the formatted tooltip value as a string.
   */
  getFormattedTooltipValue(
    value: any,
    type: string,
    config: WidgetConfig,
    tooltipItem: any,
    unitStyle: UnitStyle = 'short'
  ): string | string[] {
    if (tooltipItem.dataset.regression) {
      return this.getXYToolTip(
        config,
        tooltipItem,
        tooltipItem.dataset.r2,
        type === 'bubble'
      );
    }
    if (type === 'scatter' || type === 'bubble') {
      return this.getXYToolTip(
        config,
        tooltipItem,
        undefined,
        type === 'bubble'
      );
    }
    const indicatorIds: string[] = config.indicators;
    const idx =
      config.groupProperty === 'indicator'
        ? tooltipItem.dataset.compIndex !== undefined
          ? tooltipItem.dataset.compIndex
          : tooltipItem.dataIndex
        : tooltipItem.dataset.legendIndex !== undefined
          ? tooltipItem.dataset.legendIndex
          : tooltipItem.datasetIndex;

    return this.globalsStore.getFormattedValue(
      value,
      config.focusProperty === 'indicator'
        ? config.indicator
        : indicatorIds[idx],
      false,
      config.context === 'external'
        ? config.wegweiserData?.indicators[idx]?.unit
        : undefined,
      unitStyle,
      config.context === 'external'
    );
  }

  /**
   * Generates a formatted tooltip string based on the regression data provided.
   *
   * @param {WidgetConfig} config - The widget configuration containing indicators and context information.
   * @param {any} tooltipItem - The tooltip data item containing raw values for each axis.
   * @param {string} r2 - The regression-value.
   * @param {boolean} isBubble - Is the chart of type 'bubble'
   * @return {string} A string representing the formatted tooltip with axis labels and corresponding values.
   */
  getXYToolTip(
    config: WidgetConfig,
    tooltipItem: any,
    r2?: number,
    isBubble = false
  ): string[] {
    const SHORT_UNIT_STYLE = 'short'; // Extracted constant for unit style

    // Helper function to format a single axis value
    const formatAxisValue = (
      axis: string,
      value: any,
      indicatorId: string
    ): string =>
      this.globalsStore.getFormattedValue(
        value,
        indicatorId,
        false,
        undefined,
        SHORT_UNIT_STYLE,
        config.context === 'external'
      );

    // Map axis values to formatted output
    const formattedAxisValues = Object.entries(tooltipItem.raw).map(
      ([axis, value], indicatorIndex) => {
        const indicatorId =
          config.indicators[indicatorIndex === 3 ? 2 : indicatorIndex];
        return formatAxisValue(axis, value, indicatorId);
      }
    );

    // Add regression value if available
    if (r2) {
      formattedAxisValues.push(this.globalsStore.formatPlainValue(r2, 2));
    } else if (formattedAxisValues.length > (isBubble ? 4 : 2)) {
      formattedAxisValues.splice(2, formattedAxisValues.length - 2);
    }

    // Generate the output object (improving type accuracy based on return shape)
    return formattedAxisValues.map((value: string, idx: number) => {
      let prefix = '';
      let suffix = '';
      switch (idx) {
        case 0:
          prefix = 'X-Achse: ';
          suffix = this.indicatorStore.getById(config.indicators[0])?.name;
          break;
        case 1:
          prefix = 'Y-Achse: ';
          suffix = this.indicatorStore.getById(config.indicators[1])?.name;
          break;
        case 2:
          prefix = r2 ? 'R²: ' : 'r: ';
          suffix = r2 ? 'Regression' : 'Radius';
          break;
        case 3:
          prefix = 'Blase: ';
          suffix = this.indicatorStore.getById(config.indicators[2])?.name;
          break;
      }
      return `<div><strong>${prefix}</strong>${value} <i>[${suffix}]</i></div>`;
    });
  }
}
